import React, { lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";

// 🌟 CRITICAL NEW IMPORT 🌟
import { HelmetProvider } from 'react-helmet-async';
// 🛑 EAGER IMPORTS KEPT: Only keep imports needed for layout, context, and the login/signup screens (as they are usually small/initial entry points).
import Navbar, { ThemeProvider } from "./components/ui/Navbar";
import Footer from "./components/ui/Footer";
import ProtectedRoute from "./components/ProtectedRoute";
import RedirectIfAuth from "./components/RedirectIfAuth";
import ScrollToTop from "./components/ScrollToTop";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";

// --- Context Providers ---
import { AuthProvider } from "./contexts/AuthContext";
import { FileProvider } from "./contexts/FileContext";

// ---------- LAZY LOAD ALL HEAVY PAGES (PDF Tools and Utility Pages) ----------

// Core Pages
const HomePage = lazy(() => import("./pages/HomePage"));
const AllToolsPage = lazy(() => import('./pages/AllToolsPage'));

// Utility/Legal Pages
const ContactUsPage = lazy(() => import('./pages/ContactUsPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const SecurityPage = lazy(() => import('./pages/SecurityPage'));
const TermsAndConditionsPage = lazy(() => import('./pages/TermsAndConditionsPage'));
const AboutUsPage = lazy(() => import('./pages/AboutUsPage'));

// PDF Tool Pages
const MergePdfPage = lazy(() => import("./pages/MergePdfPage"));
const PptxToPdfPage = lazy(() => import("./pages/PptxToPdfPage"));
const SplitPdfPage = lazy(() => import("./pages/SplitPdfPage"));
const JpgToPdfPage = lazy(() => import("./pages/JpgToPdfPage"));
const HtmlToPdfPage = lazy(() => import("./pages/HtmlToPdfPage"));
const WordToPdfPage = lazy(() => import("./pages/WordToPdfPage"));
const ProtectPdfPage = lazy(() => import("./pages/ProtectPdfPage"));
const NumberPagesPage = lazy(() => import("./pages/NumberPagesPage"));
const WatermarkPage = lazy(() => import("./pages/WatermarkPage"));
const RotatePdfPage = lazy(() => import("./pages/RotatePdfPage"));
const DeletePagesPage = lazy(() => import("./pages/DeletePagesPage"));
const OrganizePdfPage = lazy(() => import("./pages/OrganizePdfPage"));
const PdfToWordPage = lazy(() => import("./pages/PdfToWordPage"));
const SignPdfPage = lazy(() => import("./pages/SignPdfPage"));
const PdfToJpgPage = lazy(() => import("./pages/PdfToJpgPage"));
const RedactPdfPage = lazy(() => import("./pages/RedactPdfPage"));
const CropPdfPage = lazy(() => import("./pages/CropPdfPage"));
const PdfToPptxPage = lazy(() => import("./pages/PdfToPptxPage"));
const PdfToExcelPage = lazy(() => import("./pages/PdfToExcelPage"));


// ---------- Data Structure for Tool Routes (using Component references) ----------
const toolRoutesData = [
  { path: "/merge-pdf", component: MergePdfPage },
  { path: "/pptx-to-pdf", component: PptxToPdfPage },
  { path: "/split-pdf", component: SplitPdfPage },
  { path: "/jpg-to-pdf", component: JpgToPdfPage },
  { path: "/html-to-pdf", component: HtmlToPdfPage },
  { path: "/word-to-pdf", component: WordToPdfPage },
  { path: "/protect-pdf", component: ProtectPdfPage },
  { path: "/number-pages", component: NumberPagesPage },
  { path: "/add-watermark", component: WatermarkPage },
  { path: "/rotate-pdf", component: RotatePdfPage },
  { path: "/delete-pages", component: DeletePagesPage },
  { path: "/organize-pdf", component: OrganizePdfPage },
  { path: "/pdf-to-word", component: PdfToWordPage },
  { path: "/sign-pdf", component: SignPdfPage },
  { path: "/pdf-to-jpg", component: PdfToJpgPage },
  { path: "/redact-pdf", component: RedactPdfPage },
  { path: "/crop-pdf", component: CropPdfPage },
  { path: "/pdf-to-pptx", component: PdfToPptxPage },
  { path: "/pdf-to-excel", component: PdfToExcelPage },
];


// ---------- Main Layout (padding for navbar) ----------
const MainLayout = ({ children }) => (
  <main
    className="app-main"
    style={{
      paddingTop: "95px",
      minHeight: "80vh",
    }}
  >
    {children}
  </main>
);

// ---------- Wrapper to show / hide Navbar + Footer ----------
const LayoutHandler = ({ children }) => {
  const location = useLocation();
  const isAuthPage =
    location.pathname === "/login" || location.pathname === "/signup";

  return (
    <>
      {!isAuthPage && <Navbar />}
      {children}
      {!isAuthPage && <Footer />}
    </>
  );
};

function App() {
  // Define a loading fallback component for Suspense
  const LoadingFallback = () => (
    <MainLayout>
      <div className="loading-state text-center pt-20 text-lg text-gray-500 dark:text-gray-400">
        Loading Tool...
      </div>
    </MainLayout>
  );


  return (
    // 🌟 HELMET PROVIDER WRAPS EVERYTHING 🌟
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <AuthProvider>
          <FileProvider>
            <ThemeProvider>
              <LayoutHandler>
                <Suspense fallback={<LoadingFallback />}>
                  <Routes>
                    {/* Auth Routes (Non-Layout) */}
                    <Route
                      path="/login"
                      element={<RedirectIfAuth><LoginPage /></RedirectIfAuth>}
                    />

                    <Route
                      path="/signup"
                      element={<RedirectIfAuth><SignupPage /></RedirectIfAuth>}
                    />

                    {/* Public Utility/Legal Pages (Wrapped in MainLayout) */}
                    <Route path="/contact" element={<MainLayout><ContactUsPage /></MainLayout>} />
                    <Route path="/all-tools" element={<MainLayout><AllToolsPage /></MainLayout>} />
                    <Route path="/privacy" element={<MainLayout><PrivacyPolicyPage /></MainLayout>} />
                    <Route path="/security" element={<MainLayout><SecurityPage /></MainLayout>} />
                    <Route path="/terms" element={<MainLayout><TermsAndConditionsPage /></MainLayout>} />
                    <Route path="/about" element={<MainLayout><AboutUsPage /></MainLayout>} />

                    {/* Home Page */}
                    <Route path="/" element={<MainLayout><HomePage /></MainLayout>} />

                    {/* 🛑 Protected Tool Routes (PROTECTED ROUTE RESTORED) 🛑 */}
                    {toolRoutesData.map(({ path, component: Component }) => (
                      <Route
                        key={path}
                        path={path}
                        element={
                          <ProtectedRoute>
                            <MainLayout><Component /></MainLayout>
                          </ProtectedRoute>
                        }
                      />
                    ))}

                    {/* Fallback */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Suspense>
              </LayoutHandler>
            </ThemeProvider>
          </FileProvider>
        </AuthProvider>
      </Router>
    </HelmetProvider>
  );
}

export default App;